/**
 * Service Worker Registration for Fastlane LV PWA
 * ─────────────────────────────────────────────────
 * Fires these custom window events:
 *   'sw:updated'   → new version is waiting (pass skipWaiting to activate)
 *   'sw:installed' → first-time install complete (app is now offline-capable)
 *   'sw:error'     → registration failed
 */

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[PWA] Service Workers not supported in this browser.');
    return;
  }

  window.addEventListener('load', async () => {
    try {
      const registration = await navigator.serviceWorker.register(
        `${process.env.PUBLIC_URL}/service-worker.js`,
        { scope: '/' }
      );

      console.log('[PWA] Service Worker registered:', registration.scope);

      // ── First install ──────────────────────────────────────────────────────
      if (registration.installing && !navigator.serviceWorker.controller) {
        const worker = registration.installing;
        worker.addEventListener('statechange', () => {
          if (worker.state === 'activated') {
            console.log('[PWA] Service Worker activated — app is now offline-capable!');
            window.dispatchEvent(new CustomEvent('sw:installed'));
          }
        });
      }

      // ── Update found ───────────────────────────────────────────────────────
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;

        newWorker.addEventListener('statechange', () => {
          // New SW installed and waiting — prompt user to refresh
          if (
            newWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            console.log('[PWA] New version available — waiting for activation');
            window.dispatchEvent(
              new CustomEvent('sw:updated', {
                detail: {
                  skipWaiting: () => {
                    newWorker.postMessage({ type: 'SKIP_WAITING' });
                  },
                },
              })
            );
          }
        });
      });

      // ── Reload ONCE after a GENUINE update takes control ──────────────────
      // Only reload if a controller already existed when the page loaded (a real
      // version update). On the very FIRST install the new SW claims the page via
      // clients.claim(), which ALSO fires 'controllerchange' — reloading there made
      // every brand-new visit / cleared-cache / incognito load look like it "loaded
      // twice". Skipping that removes the double-load, while a post-deploy update
      // still reloads so the page picks up the new bundle and never hits a
      // ChunkLoadError trying to lazy-load an old chunk that the new SW purged.
      const hadControllerAtLoad = !!navigator.serviceWorker.controller;
      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (refreshing || !hadControllerAtLoad) return;
        refreshing = true;
        window.location.reload();
      });

      // ── Periodic update check (every hour in production) ───────────────────
      // NOTE: the try/catch below has already returned by the time this fires an
      // hour later, so the .catch here is the only thing standing between an
      // offline blip and a window-level unhandled rejection.
      if (process.env.NODE_ENV === 'production') {
        setInterval(() => {
          registration.update().catch((err) => {
            console.warn('[PWA] SW update check failed:', err);
          });
        }, 60 * 60 * 1000);
      }
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      window.dispatchEvent(new CustomEvent('sw:error', { detail: error }));
    }
  });
}

export function unregisterServiceWorker() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch(console.error);
  }
}
